import nurseryData from './nurseries.json';

export interface PlantInventory {
  plantId: string;
  name: string;
  scientificName: string;
  category: 'native' | 'invasive' | 'cultivar' | 'non-native-non-invasive';
  ecologicalValue: number; // 1-10 scale for ecological benefit
  price: number;
  availability: 'in-stock' | 'limited' | 'out-of-stock' | 'seasonal';
  description: string;
}

export interface EcologicalMetrics {
  nativePlantPercentage: number;
  invasivePlantPercentage: number;
  cultivarPercentage: number;
  nonNativeNonInvasivePercentage: number;
  sustainabilityPractices: string[];
  organicPractices: boolean;
  localSourcing: boolean;
  educationalPrograms: boolean;
  invasiveSpeciesPolicy: 'strict' | 'moderate' | 'minimal' | 'none';
  nativePlantPromotion: boolean;
  waterConservation: boolean;
  pesticidePolicy: 'none' | 'limited' | 'moderate' | 'unrestricted';
}

export interface Nursery {
  id: string;
  name: string;
  address: string;
  phone: string;
  website: string;
  email: string;
  specialties: string[];
  services: string[];
  hours: {
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
    sunday: string;
  };
  description: string;
  established: number;
  certifications: string[];
  deliveryAvailable: boolean;
  consultationAvailable: boolean;
  priceRange: string; // Changed from literal types to string for JSON compatibility
  rating: number;
  reviewCount: number;
  plantInventory: PlantInventory[];
  ecologicalMetrics: EcologicalMetrics;
}

// Type assertion function to ensure proper typing of imported JSON data
const assertNurseryData = (data: any[]): Nursery[] => {
  return data.map(nursery => ({
    ...nursery,
    priceRange: nursery.priceRange as 'low' | 'medium' | 'high',
    ecologicalMetrics: {
      nativePlantPercentage: nursery.ecologicalMetrics?.nativePlantPercentage || 0,
      invasivePlantPercentage: nursery.ecologicalMetrics?.invasivePlantPercentage || 0,
      cultivarPercentage: nursery.ecologicalMetrics?.cultivarPercentage || 0,
      nonNativeNonInvasivePercentage: nursery.ecologicalMetrics?.nonNativeNonInvasivePercentage || 0,
      sustainabilityPractices: nursery.ecologicalMetrics?.sustainabilityPractices || [],
      organicPractices: nursery.ecologicalMetrics?.organicPractices || false,
      localSourcing: nursery.ecologicalMetrics?.localSourcing || false,
      educationalPrograms: nursery.ecologicalMetrics?.educationalPrograms || false,
      invasiveSpeciesPolicy: (nursery.ecologicalMetrics?.invasiveSpeciesPolicy || 'none') as 'strict' | 'moderate' | 'minimal' | 'none',
      nativePlantPromotion: nursery.ecologicalMetrics?.nativePlantPromotion || false,
      waterConservation: nursery.ecologicalMetrics?.waterConservation || false,
      pesticidePolicy: (nursery.ecologicalMetrics?.pesticidePolicy || 'unrestricted') as 'none' | 'limited' | 'moderate' | 'unrestricted'
    },
    plantInventory: (nursery.plantInventory || []).map((plant: any) => ({
      ...plant,
      category: plant.category as 'native' | 'invasive' | 'cultivar' | 'non-native-non-invasive',
      availability: plant.availability as 'in-stock' | 'limited' | 'out-of-stock' | 'seasonal'
    }))
  }));
};

export const nurseries = assertNurseryData(nurseryData);

export const nurseryCategories = {
  nativeSpecialists: 'Native Plant Specialists',
  generalNurseries: 'General Nurseries with Native Selection',
  wholesale: 'Wholesale Nurseries',
  specialty: 'Specialty Nurseries'
};

export const nurseryServices = {
  delivery: 'Delivery Service',
  consultation: 'Garden Consultation',
  landscaping: 'Landscaping Services',
  workshops: 'Educational Workshops',
  onlineOrdering: 'Online Ordering',
  plantGuarantee: 'Plant Guarantee'
};

export const calculateGreenScore = (nursery: Nursery): number => {
  const metrics = nursery.ecologicalMetrics;
  let score = 0;
  
  // Native plant percentage (0-40 points)
  score += (metrics.nativePlantPercentage || 0) * 0.4;
  
  // Invasive plant penalty (0-20 points, inverted)
  score += (100 - (metrics.invasivePlantPercentage || 0)) * 0.2;
  
  // Sustainable practices (0-15 points) - with defensive check
  const sustainabilityPractices = metrics.sustainabilityPractices || [];
  score += Math.min(sustainabilityPractices.length * 3, 15);
  
  // Organic practices (0-10 points)
  score += metrics.organicPractices ? 10 : 0;
  
  // Local sourcing (0-5 points)
  score += metrics.localSourcing ? 5 : 0;
  
  // Educational programs (0-5 points)
  score += metrics.educationalPrograms ? 5 : 0;
  
  // Invasive species policy (0-10 points)
  const policyScores = { strict: 10, moderate: 7, minimal: 3, none: 0 };
  score += policyScores[metrics.invasiveSpeciesPolicy] || 0;
  
  // Native plant promotion (0-5 points)
  score += metrics.nativePlantPromotion ? 5 : 0;
  
  // Water conservation (0-5 points)
  score += metrics.waterConservation ? 5 : 0;
  
  // Pesticide policy (0-5 points)
  const pesticideScores = { none: 5, limited: 3, moderate: 1, unrestricted: 0 };
  score += pesticideScores[metrics.pesticidePolicy] || 0;
  
  return Math.round(score);
};

export const getEcologicalRating = (greenScore: number): { rating: string; color: string; description: string } => {
  if (greenScore >= 90) {
    return { rating: 'A+', color: '#10b981', description: 'Exceptionally Green' };
  } else if (greenScore >= 80) {
    return { rating: 'A', color: '#059669', description: 'Highly Green' };
  } else if (greenScore >= 70) {
    return { rating: 'B+', color: '#84cc16', description: 'Very Green' };
  } else if (greenScore >= 60) {
    return { rating: 'B', color: '#65a30d', description: 'Green' };
  } else if (greenScore >= 50) {
    return { rating: 'C+', color: '#eab308', description: 'Moderately Green' };
  } else if (greenScore >= 40) {
    return { rating: 'C', color: '#ca8a04', description: 'Somewhat Green' };
  } else if (greenScore >= 30) {
    return { rating: 'D', color: '#f59e0b', description: 'Limited Green Practices' };
  } else {
    return { rating: 'F', color: '#dc2626', description: 'Poor Green Practices' };
  }
};